/**
 * BackgroundCheckBanner — banner persistente para lojistas/motoboys que ainda
 * não têm o background check aprovado.
 *
 * - Polling leve a cada 60s em GET /api/background-check/me.
 * - Aparece apenas se `required === true` e `status !== "aprovado"`.
 * - Cores e mensagem mudam conforme status (em revisão, consultando, reprovado,
 *   expirado, sem registro).
 * - Dismiss soft: usuário pode esconder por 24h via localStorage.
 *
 * Por que persistente?
 * Antes deste banner, o lojista descobria o bloqueio só na hora de aceitar um
 * pedido (toast de erro). Agora ele vê a pendência ao logar e tem CTA direto
 * para /verificacao.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, ShieldCheck, ShieldQuestion, Clock, X, ArrowRight } from "lucide-react";
import { API } from "../App";

const DISMISS_KEY = "bgcheck_banner_dismissed_until";

// Mapeia status → cor, ícone e copy
const STATUS_VARIANTS = {
  // Não há registro ainda
  missing: {
    bg: "from-orange-500 to-red-500",
    icon: ShieldAlert,
    title: "Verificação de antecedentes pendente",
    subtitle: "Você precisa concluir a verificação para aceitar pedidos. Leva menos de 2 minutos.",
    cta: "Fazer verificação agora",
  },
  aguardando_dados: {
    bg: "from-orange-500 to-amber-500",
    icon: ShieldAlert,
    title: "Verificação iniciada — falta enviar seus dados",
    subtitle: "Complete o cadastro com CPF e dados pessoais.",
    cta: "Completar verificação",
  },
  consultando: {
    bg: "from-blue-500 to-indigo-500",
    icon: Clock,
    title: "Verificação em análise",
    subtitle: "Estamos consultando seus dados. Em alguns instantes liberaremos o acesso.",
    cta: "Ver status",
  },
  em_revisao: {
    bg: "from-yellow-500 to-orange-500",
    icon: ShieldQuestion,
    title: "Verificação em revisão manual",
    subtitle: "Nossa equipe está revisando seu caso. Você será notificado em até 24h úteis.",
    cta: "Ver detalhes",
  },
  reprovado: {
    bg: "from-red-600 to-red-700",
    icon: ShieldAlert,
    title: "Verificação reprovada",
    subtitle: "Entre em contato com o suporte ou refaça a verificação com dados atualizados.",
    cta: "Ver motivo e refazer",
  },
  expirado: {
    bg: "from-amber-500 to-orange-500",
    icon: ShieldAlert,
    title: "Sua verificação expirou",
    subtitle: "A validade é de 90 dias. Refaça agora para continuar aceitando pedidos.",
    cta: "Refazer verificação",
  },
};

export default function BackgroundCheckBanner({ user }) {
  const [state, setState] = useState({ loading: true, required: false, status: null });
  const [dismissed, setDismissed] = useState(false);

  // Checa dismiss timestamp (24h)
  useEffect(() => {
    try {
      const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (until && Date.now() < until) setDismissed(true);
    } catch (_e) { void _e; }
  }, []);

  useEffect(() => {
    // Só faz sentido pra driver/merchant
    if (!user || !["merchant", "driver"].includes(user.user_type)) {
      setState({ loading: false, required: false, status: null });
      return;
    }
    let cancelled = false;
    async function fetchStatus() {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await fetch(`${API}/background-check/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && res.ok) {
          const data = await res.json();
          setState({
            loading: false,
            required: !!data.required,
            status: data.check?.status || null,
          });
        }
      } catch (_e) { void _e; }
    }
    fetchStatus();
    const id = setInterval(fetchStatus, 60000); // 1min polling
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  if (state.loading || !state.required || dismissed) return null;
  // Aprovado → nada a mostrar
  if (state.status === "aprovado") return null;

  const variant = STATUS_VARIANTS[state.status] || STATUS_VARIANTS.missing;
  const Icon = variant.icon;

  function dismiss24h() {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    } catch (_e) { void _e; }
    setDismissed(true);
  }

  return (
    <div
      className={`relative w-full bg-gradient-to-r ${variant.bg} text-white shadow-lg`}
      data-testid="background-check-banner"
      role="alert"
    >
      <div className="container mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5"/>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="font-bold text-sm leading-tight" data-testid="background-check-banner-title">
            {variant.title}
          </div>
          <div className="text-xs text-white/90 mt-0.5">{variant.subtitle}</div>
        </div>
        <Link
          to="/verificacao"
          className="bg-white/20 hover:bg-white/30 backdrop-blur px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2 transition-colors"
          data-testid="background-check-banner-cta"
        >
          {variant.cta} <ArrowRight className="w-4 h-4"/>
        </Link>
        <button
          onClick={dismiss24h}
          className="w-8 h-8 rounded-lg hover:bg-white/15 flex items-center justify-center shrink-0"
          aria-label="Esconder por 24h"
          data-testid="background-check-banner-dismiss"
        >
          <X className="w-4 h-4"/>
        </button>
      </div>
    </div>
  );
}

// Re-export pra uso em outros shells (driver, futuro)
export { BackgroundCheckBanner };
